import { useNavigate } from 'react-router-dom'
import { DataTableColumn } from '@/components/common/DataTable'
import { ColumnConfig } from '@/components/ui/ColumnVisibilityControl'
import { LockedSalaryData } from '@/types/lockedSalaryData'
import { getAmountToPay } from '@/services/lockedDataAdapter'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { formatSEK } from '@/lib/formatters'
import { Mail, Download, Check, Loader2 } from 'lucide-react'

/**
 * Column configuration for the Export page DataTable
 * Matches the SalaryReportPage columns using locked salary data
 */

// Default columns for export table - mirrors DEFAULT_SALARY_COLUMNS from reportConfig
export const DEFAULT_EXPORT_COLUMNS: ColumnConfig[] = [
  { key: 'driverName', label: 'Förare', visible: true, required: true, align: 'left' },
  { key: 'personalCode', label: 'Personnummer', visible: false, align: 'left' },
  { key: 'email', label: 'E-post', visible: false, align: 'left' },
  { key: 'grossEarnings', label: 'Bruttointäkter', visible: true, align: 'right' },
  { key: 'netEarnings', label: 'Nettointäkter', visible: false, align: 'right' },
  { key: 'tips', label: 'Dricks', visible: true, align: 'right' },
  { key: 'cashReceived', label: 'Kontanter mottagna', visible: false, align: 'right' },
  { key: 'vatAmount', label: 'Moms', visible: false, align: 'right' },
  { key: 'shareableAmount', label: 'Delbart belopp', visible: false, align: 'right' },
  { key: 'afterFleetCommission', label: 'Förarandel (exkl. semester)', visible: false, align: 'right' },
  { key: 'vacationPay', label: 'Semesterersättning', visible: false, align: 'right' },
  { key: 'finalGrossSalary', label: 'Bruttolön', visible: true, align: 'right' },
  { key: 'employerTaxAmount', label: 'Arbetsgivaravgift', visible: false, align: 'right' },
  { key: 'totalEmployerCost', label: 'Total lönekostnad', visible: false, align: 'right' },
  { key: 'taxAmount', label: 'Skatt', visible: false, align: 'right' },
  { key: 'effectiveTaxRate', label: 'Skatt (%)', visible: false, align: 'right' },
  { key: 'netSalary', label: 'Nettolön', visible: false, align: 'right' },
  { key: 'amountToPay', label: 'Att utbetala', visible: true, align: 'right' },
  { key: 'emailStatus', label: 'Status', visible: true, align: 'center' },
  { key: 'actions', label: 'Åtgärder', visible: true, required: true, align: 'center' }
]

// Email status badge component
interface EmailStatusBadgeProps {
  data: LockedSalaryData
}

export function EmailStatusBadge({ data }: EmailStatusBadgeProps) {
  if (data.emailSentAt) {
    return (
      <Badge variant="default" className="bg-green-600">
        <Check className="h-3 w-3 mr-1" />
        Skickad
      </Badge>
    )
  }

  if (data.driverInfo.email) {
    return <Badge variant="secondary">Väntar</Badge>
  }

  return (
    <Badge variant="outline" className="text-muted-foreground">
      Saknar email
    </Badge>
  )
}

// Clickable driver name cell
function DriverNameCell({ data }: { data: LockedSalaryData }) {
  const navigate = useNavigate()

  return (
    <button
      className="hover:text-blue-600 hover:underline text-left font-medium"
      onClick={(e) => {
        e.stopPropagation()
        navigate(`/driver/${data.driverId}`)
      }}
    >
      {data.driverInfo.name}
    </button>
  )
}

// Actions cell props
interface ActionsCellProps {
  data: LockedSalaryData
  sendingEmailTo: string | null
  downloadingPdfFor: string | null
  onSendEmail: (data: LockedSalaryData) => void
  onDownloadPdf: (data: LockedSalaryData) => void
}

// Actions cell component
function ActionsCell({
  data,
  sendingEmailTo,
  downloadingPdfFor,
  onSendEmail,
  onDownloadPdf
}: ActionsCellProps) {
  const isSending = sendingEmailTo === data.driverId
  const isDownloading = downloadingPdfFor === data.driverId
  const hasEmail = !!data.driverInfo.email
  const alreadySent = !!data.emailSentAt

  return (
    <div className="flex items-center justify-center gap-2">
      <Button
        variant="outline"
        size="sm"
        onClick={(e) => {
          e.stopPropagation()
          onSendEmail(data)
        }}
        disabled={!hasEmail || isSending}
        title={
          !hasEmail
            ? 'Föraren saknar e-postadress'
            : alreadySent
              ? 'Skicka igen'
              : 'Skicka lönespecifikation via email'
        }
      >
        {isSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={(e) => {
          e.stopPropagation()
          onDownloadPdf(data)
        }}
        disabled={isDownloading}
        title="Ladda ner PDF"
      >
        {isDownloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
      </Button>
    </div>
  )
}

// Create all column definitions for the export table
export function createExportColumns(
  sendingEmailTo: string | null,
  downloadingPdfFor: string | null,
  onSendEmail: (data: LockedSalaryData) => void,
  onDownloadPdf: (data: LockedSalaryData) => void
): DataTableColumn<LockedSalaryData>[] {
  return [
    {
      key: 'driverInfo' as keyof LockedSalaryData,
      header: 'Förare',
      sortable: true,
      align: 'left',
      cell: (_value, data) => <DriverNameCell data={data} />,
      exportValue: (data) => data.driverInfo.name
    },
    {
      key: 'driverInfo' as keyof LockedSalaryData,
      header: 'Personnummer',
      sortable: false,
      align: 'left',
      cell: (_value, data) => data.driverInfo.personalCode || '-',
      exportValue: (data) => data.driverInfo.personalCode || ''
    },
    {
      key: 'driverInfo' as keyof LockedSalaryData,
      header: 'E-post',
      sortable: false,
      align: 'left',
      cell: (_value, data) => data.driverInfo.email || '-',
      exportValue: (data) => data.driverInfo.email || ''
    },
    {
      key: 'aggregatedTotals' as keyof LockedSalaryData,
      header: 'Bruttointäkter',
      sortable: true,
      align: 'right',
      cell: (_value, data) => formatSEK(data.aggregatedTotals.grossEarnings),
      exportValue: (data) => data.aggregatedTotals.grossEarnings
    },
    {
      key: 'aggregatedTotals' as keyof LockedSalaryData,
      header: 'Nettointäkter',
      sortable: true,
      align: 'right',
      cell: (_value, data) => formatSEK(data.aggregatedTotals.netEarnings),
      exportValue: (data) => data.aggregatedTotals.netEarnings
    },
    {
      key: 'aggregatedTotals' as keyof LockedSalaryData,
      header: 'Dricks',
      sortable: true,
      align: 'right',
      cell: (_value, data) => formatSEK(data.aggregatedTotals.tips),
      exportValue: (data) => data.aggregatedTotals.tips
    },
    {
      key: 'aggregatedTotals' as keyof LockedSalaryData,
      header: 'Kontanter mottagna',
      sortable: true,
      align: 'right',
      cell: (_value, data) => formatSEK(data.aggregatedTotals.cashReceived),
      exportValue: (data) => data.aggregatedTotals.cashReceived
    },
    {
      key: 'aggregatedTotals' as keyof LockedSalaryData,
      header: 'Moms',
      sortable: true,
      align: 'right',
      cell: (_value, data) => formatSEK(data.aggregatedTotals.vatAmount),
      exportValue: (data) => data.aggregatedTotals.vatAmount
    },
    {
      key: 'aggregatedTotals' as keyof LockedSalaryData,
      header: 'Delbart belopp',
      sortable: true,
      align: 'right',
      cell: (_value, data) => formatSEK(data.aggregatedTotals.shareableAmount),
      exportValue: (data) => data.aggregatedTotals.shareableAmount
    },
    {
      key: 'aggregatedTotals' as keyof LockedSalaryData,
      header: 'Förarandel (exkl. semester)',
      sortable: true,
      align: 'right',
      cell: (_value, data) => formatSEK(data.aggregatedTotals.afterFleetCommission),
      exportValue: (data) => data.aggregatedTotals.afterFleetCommission
    },
    {
      key: 'aggregatedTotals' as keyof LockedSalaryData,
      header: 'Semesterersättning',
      sortable: true,
      align: 'right',
      cell: (_value, data) => formatSEK(data.aggregatedTotals.vacationPay),
      exportValue: (data) => data.aggregatedTotals.vacationPay
    },
    {
      key: 'totalGrossSalary' as keyof LockedSalaryData,
      header: 'Bruttolön',
      sortable: true,
      align: 'right',
      cell: (_value, data) => formatSEK(data.totalGrossSalary),
      exportValue: (data) => data.totalGrossSalary
    },
    {
      key: 'aggregatedTotals' as keyof LockedSalaryData,
      header: 'Arbetsgivaravgift',
      sortable: true,
      align: 'right',
      cell: (_value, data) => formatSEK(data.aggregatedTotals.employerTaxAmount),
      exportValue: (data) => data.aggregatedTotals.employerTaxAmount
    },
    {
      key: 'aggregatedTotals' as keyof LockedSalaryData,
      header: 'Total lönekostnad',
      sortable: true,
      align: 'right',
      cell: (_value, data) => (
        <span className="font-semibold">{formatSEK(data.aggregatedTotals.totalEmployerCost)}</span>
      ),
      exportValue: (data) => data.aggregatedTotals.totalEmployerCost
    },
    {
      key: 'aggregatedTotals' as keyof LockedSalaryData,
      header: 'Skatt',
      sortable: true,
      align: 'right',
      cell: (_value, data) => formatSEK(data.aggregatedTotals.taxAmount),
      exportValue: (data) => data.aggregatedTotals.taxAmount
    },
    {
      key: 'aggregatedTotals' as keyof LockedSalaryData,
      header: 'Skatt (%)',
      sortable: true,
      align: 'right',
      cell: (_value, data) => `${data.aggregatedTotals.effectiveTaxRate.toFixed(1)}%`,
      exportValue: (data) => data.aggregatedTotals.effectiveTaxRate
    },
    {
      key: 'aggregatedTotals' as keyof LockedSalaryData,
      header: 'Nettolön',
      sortable: true,
      align: 'right',
      cell: (_value, data) => formatSEK(data.aggregatedTotals.netSalary),
      exportValue: (data) => data.aggregatedTotals.netSalary
    },
    {
      key: 'aggregatedTotals' as keyof LockedSalaryData,
      header: 'Att utbetala',
      sortable: true,
      align: 'right',
      cell: (_value, data) => {
        const totalPayout = getAmountToPay(data.aggregatedTotals)
        const untaxedTips = data.aggregatedTotals.untaxedTips || 0
        const salaryPayout = totalPayout - untaxedTips
        return (
          <div className="text-right">
            <div className="font-medium text-green-700">{formatSEK(untaxedTips > 0 ? salaryPayout : totalPayout)}</div>
            {untaxedTips > 0 && (
              <div className="text-xs text-green-600">
                +{formatSEK(untaxedTips)} dricks
              </div>
            )}
            {data.aggregatedTotals.cashReceived > 0 && (
              <div className="text-xs text-muted-foreground">
                Efter {formatSEK(data.aggregatedTotals.cashReceived)} kontant förskott
              </div>
            )}
          </div>
        )
      },
      exportValue: (data) => {
        const totalPayout = getAmountToPay(data.aggregatedTotals)
        const untaxedTips = data.aggregatedTotals.untaxedTips || 0
        return totalPayout - untaxedTips
      }
    },
    {
      key: 'emailSentAt' as keyof LockedSalaryData,
      header: 'Status',
      sortable: true,
      align: 'center',
      cell: (_value, data) => <EmailStatusBadge data={data} />,
      exportValue: (data) => (data.emailSentAt ? 'Skickad' : data.driverInfo.email ? 'Väntar' : 'Saknar email')
    },
    {
      key: 'id' as keyof LockedSalaryData,
      header: 'Åtgärder',
      sortable: false,
      align: 'center',
      cell: (_value, data) => (
        <ActionsCell
          data={data}
          sendingEmailTo={sendingEmailTo}
          downloadingPdfFor={downloadingPdfFor}
          onSendEmail={onSendEmail}
          onDownloadPdf={onDownloadPdf}
        />
      )
    }
  ]
}

// Map column config keys to actual column indices
const COLUMN_KEY_MAP: Record<string, number> = {
  driverName: 0,
  personalCode: 1,
  email: 2,
  grossEarnings: 3,
  netEarnings: 4,
  tips: 5,
  cashReceived: 6,
  vatAmount: 7,
  shareableAmount: 8,
  afterFleetCommission: 9,
  vacationPay: 10,
  finalGrossSalary: 11,
  employerTaxAmount: 12,
  totalEmployerCost: 13,
  taxAmount: 14,
  effectiveTaxRate: 15,
  netSalary: 16,
  amountToPay: 17,
  emailStatus: 18,
  actions: 19
}

// Get visible columns based on configuration
export function getVisibleExportColumns(
  visibleColumnKeys: string[],
  sendingEmailTo: string | null,
  downloadingPdfFor: string | null,
  onSendEmail: (data: LockedSalaryData) => void,
  onDownloadPdf: (data: LockedSalaryData) => void
): DataTableColumn<LockedSalaryData>[] {
  const allColumns = createExportColumns(sendingEmailTo, downloadingPdfFor, onSendEmail, onDownloadPdf)
  return visibleColumnKeys
    .map((key) => {
      const index = COLUMN_KEY_MAP[key]
      return index !== undefined ? allColumns[index] : null
    })
    .filter((col): col is DataTableColumn<LockedSalaryData> => col !== null)
}
